import { useTheme } from '@mui/material';
import { Pause as PauseIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import { useEffect } from 'react';
import type { HistorySong, RemixerSong } from 'services/store/remixer/types';
import FormatTime from 'components/FormatTime';
import { useSharedWaveformStyles } from 'services/hooks/waveform';
import usePlayer from './usePlayer';
import {
  PlayerGrid,
  WaveformContainer,
  PlayerControlIcon,
  PlayerControl,
  DurationContainer,
  PlayerInner,
} from './styles';
import PlayerSkeleton from './PlayerSkeleton';

interface PlayerProps {
  song: RemixerSong | HistorySong | null;
}

const Player = ({ song }: PlayerProps) => {
  const theme = useTheme();
  const sharedWaveformStyles = useSharedWaveformStyles();
  const {
    ref: containerRef,
    wavesurfer,
    isPlaying,
    isLoading,
    currentTime,
    play,
    pause,
  } = usePlayer();

  const musicFilePath = song?.musicFilePath ?? null;

  useEffect(() => {
    if (!musicFilePath || !wavesurfer) {
      return;
    }
    pause();
    wavesurfer.setTime(0);
    wavesurfer.load(musicFilePath);
  }, [musicFilePath, wavesurfer, pause]);

  useEffect(() => {
    wavesurfer?.setOptions({
      waveColor: sharedWaveformStyles.waveColor,
      progressColor: sharedWaveformStyles.progressColor,
      barGap: sharedWaveformStyles.barGap,
      barWidth: sharedWaveformStyles.barWidth,
      height: 'auto',
      cursorWidth: 0,
    });
  }, [wavesurfer, theme, sharedWaveformStyles]);

  const showLoader = !song || isLoading;

  return (
    <PlayerInner>
      {showLoader && <PlayerSkeleton />}
      <PlayerGrid
        sx={{
          /*
           * In order to avoid unnecessary wavesurfer reinitialisation
           * we just hide the element instead or removing it from DOM
           * */
          ...(showLoader ? { display: 'none' } : {}),
        }}
      >
        <PlayerControl onClick={isPlaying ? pause : play}>
          {isPlaying ? (
            <PlayerControlIcon component={PauseIcon} />
          ) : (
            <PlayerControlIcon component={PlayIcon} />
          )}
        </PlayerControl>
        <DurationContainer>
          <FormatTime time={currentTime} leadingZero />
        </DurationContainer>
        <WaveformContainer ref={containerRef} />
      </PlayerGrid>
    </PlayerInner>
  );
};

export default Player;
